import React from 'react';
import { Button, Modal, Typography, Upload } from 'antd';
import { DownloadOutlined, UploadOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { OrderQuotaDetail, OrderQuotaImportResult, OrderQuotaImportRowInput } from '../graphql/orderQuotaGraphql';
import { useSheetImport } from '../hooks/useSheetImport';
import {
    FixedHeaders,
    buildTemplateWorkbook,
    downloadWorkbook,
    fixedHeaderOptions,
    parseQuotaSheet,
    templateFileName,
} from '../utils/orderQuotaExcel';
import ImportPreview, { ImportRejection } from './ImportPreview';

interface ImportQuotaModalProps {
    open: boolean;
    detail: OrderQuotaDetail;
    /** 当前网格的全部日期，模板只取今日及以后 */
    dates: string[];
    onCancel: () => void;
    /** dryRun 为 true 时只校验返回预览 */
    onImport: (rows: OrderQuotaImportRowInput[], dryRun: boolean) => Promise<OrderQuotaImportResult>;
}

/**
 * 导入弹窗：下载模板（含当前配置）→ 上传 .xlsx / .xls / .csv（读取第一个工作表）→ 预览 → 确认导入。
 * 只合并：文件中没有的对象不变；预览、确认与跳过错误行的流程见 useSheetImport。
 */
const ImportQuotaModal: React.FC<ImportQuotaModalProps> = ({ open, detail, dates, onCancel, onImport }) => {
    const { t, i18n } = useTranslation();
    /* 模板前三列表头：下载与上传校验共用 */
    const headerKeys: FixedHeaders = [
        `central.orderQuota.import.codeHeader.${detail.strategyType}`,
        'central.orderQuota.import.nameHeader',
        `central.orderQuota.rowDailyQuota.${detail.strategyType}`,
    ];
    const { preview, rejection, loading, blocked, handleFile, handleConfirm } = useSheetImport({
        open,
        parse: (data) => parseQuotaSheet(data, fixedHeaderOptions(headerKeys, (language, key) => i18n.getFixedT(language)(key))),
        onImport,
        onClose: onCancel,
    });
    const codeHeader = t(headerKeys[0]);

    return (
        <Modal
            open={open}
            width={720}
            title={t('central.orderQuota.import.title')}
            okText={t('central.orderQuota.import.confirm')}
            cancelText={t('common.cancel')}
            okButtonProps={{ disabled: blocked, loading }}
            onCancel={onCancel}
            onOk={handleConfirm}
            destroyOnClose
        >
            <div className="flex flex-col gap-4">
                <div className="flex items-center gap-3">
                    <Button
                        icon={<DownloadOutlined />}
                        onClick={() =>
                            downloadWorkbook(
                                buildTemplateWorkbook(detail, dates, {
                                    code: codeHeader,
                                    name: t(headerKeys[1]),
                                    dailyQuota: t(headerKeys[2]),
                                }),
                                templateFileName(detail.hubName || detail.hubCode, detail.today)
                            )
                        }
                    >
                        {t('central.orderQuota.import.template')}
                    </Button>
                    <Upload accept=".xlsx,.xls,.csv" showUploadList={false} beforeUpload={handleFile}>
                        <Button icon={<UploadOutlined />} loading={loading}>
                            {t('central.orderQuota.import.upload')}
                        </Button>
                    </Upload>
                </div>
                <Typography.Text type="secondary">{t(`central.orderQuota.import.blankHint.${detail.strategyType}`)}</Typography.Text>
                {rejection && <ImportRejection reason={rejection} />}
                {preview && (
                    <ImportPreview
                        preview={preview}
                        codeHeader={codeHeader}
                        summary={
                            t('central.orderQuota.import.summary', {
                                total: preview.total,
                                valid: preview.validCount,
                                created: preview.newCount,
                                updated: preview.updateCount,
                                unchanged: preview.unchangedCount,
                                errors: preview.errors.length,
                                ignored: preview.ignoredPastDateColumns,
                            }) as string
                        }
                    />
                )}
            </div>
        </Modal>
    );
};

export default ImportQuotaModal;
